/**
 * Command palette (⌘K).
 *
 * Searches navigation and people. The people half hits the real directory
 * endpoint, debounced, and is scoped by the server — a manager searching here
 * sees their reporting tree, not the whole company.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { get } from '../lib/api.js';
import { useAuth } from '../lib/auth.jsx';
import { NAV_GROUPS } from '../lib/nav.js';
import { Icon } from '../lib/icons.jsx';
import { Avatar } from './ui.jsx';

export default function CommandPalette({ onClose, onAction }) {
  const navigate = useNavigate();
  const { can } = useAuth();
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 180);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const pages = NAV_GROUPS
    .flatMap((g) => g.items)
    .filter((i) => !i.permission || can(i.permission))
    .filter((i) => !debounced || i.label.toLowerCase().includes(debounced.toLowerCase()))
    .slice(0, 7);

  const people = useQuery({
    queryKey: ['cmdk-people', debounced],
    queryFn: () => get('/employees', { q: debounced, pageSize: 6 }),
    enabled: debounced.length >= 2 && can('directory.read'),
  });

  const goto = (to) => { onClose(); navigate(to); };

  const rows = people.data?.data ?? [];
  const nothing = !pages.length && !rows.length && debounced;

  return (
    <div className="overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="cmdk" role="dialog" aria-modal="true" aria-label="Command palette">
        <div className="cmdk-in">
          <Icon name="search" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search people, pages, actions…"
            autoComplete="off"
          />
        </div>

        <div className="cmdk-list">
          {pages.length > 0 && (
            <>
              <div className="cmdk-sec">Navigate</div>
              {pages.map((p) => (
                <button key={p.id} type="button" className="cmdk-row" style={{ width: '100%' }} onClick={() => goto(p.to)}>
                  <Icon name={p.icon} />
                  <span className="rt">{p.label}</span>
                </button>
              ))}
            </>
          )}

          {rows.length > 0 && (
            <>
              <div className="cmdk-sec">People</div>
              {rows.map((e) => (
                <button
                  key={e.id}
                  type="button"
                  className="cmdk-row"
                  style={{ width: '100%' }}
                  onClick={() => goto(`/employee/${e.id}`)}
                >
                  <Avatar name={e.fullName} size="xs" />
                  <span className="rt">{e.fullName}</span>
                  <span className="rs">{e.designation}</span>
                </button>
              ))}
            </>
          )}

          {!debounced && (
            <>
              <div className="cmdk-sec">Quick actions</div>
              <button type="button" className="cmdk-row" style={{ width: '100%' }} onClick={() => { onClose(); onAction('leave'); }}>
                <Icon name="plane" /><span className="rt">Apply for leave</span>
              </button>
              <button type="button" className="cmdk-row" style={{ width: '100%' }} onClick={() => { onClose(); onAction('assistant'); }}>
                <Icon name="sparkles" /><span className="rt">Ask the HR assistant</span>
              </button>
              <button type="button" className="cmdk-row" style={{ width: '100%' }} onClick={() => { onClose(); onAction('ticket'); }}>
                <Icon name="ticket" /><span className="rt">Raise a help desk ticket</span>
              </button>
            </>
          )}

          {nothing && (
            <div className="empty" style={{ padding: 26 }}>
              <Icon name="search" />
              <h4>No matches</h4>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
